//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import { useCallback, useState } from 'react';

// A set of string ids with a toggle-membership setter — the same shape
// ChatThread's turn-trace open/close and TurnTracePanel's row expand/collapse
// each implemented independently (found in review of #988: identical
// useState<ReadonlySet<string>> plus a copy-add/delete toggle function,
// landing twice in the same PR with no existing shared hook for the
// pattern). Extracted here so further call sites reuse this instead of
// growing their own copy — `ToolSuggestions.tsx` had a third, older copy of
// this exact shape, found separately (review of #988) and pointed here too.
// `reset` is for that third call site, which clears its expanded set when
// its popup closes; the first two don't need it and can ignore it.
export const useToggleSet = (): [
  ReadonlySet<string>,
  (id: string) => void,
  () => void,
] => {
  const [ids, setIds] = useState<ReadonlySet<string>>(new Set());

  // Stable identities (useCallback) so a consumer can safely list these in a
  // dependency array — e.g. ToolSuggestions.tsx's popup-close reset effect —
  // without either an exhaustive-deps warning or a new effect run every
  // render.
  const toggle = useCallback((id: string) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => setIds(new Set()), []);

  return [ids, toggle, reset];
};
